/**
 * Markdown / HTML -> sanitized HTML.
 *
 * GFM so pipe tables in `content.value` render as real tables, and raw inline
 * HTML (e.g. `<sup>1&dagger;</sup>` in the authors block) survives the trip.
 */
import DOMPurify from 'dompurify';
import { toHtml } from 'hast-util-to-html';
import { toHast } from 'mdast-util-to-hast';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkParse from 'remark-parse';
import { unified } from 'unified';

const processor = unified().use(remarkParse).use(remarkGfm).use(remarkMath);

/**
 * Display math becomes a `div[data-math]` and inline math a `code.math-inline`.
 * Both are shapes TipTap's schema can hold — see the MathBlock extension, which
 * turns the div into a KaTeX-rendered node.
 */
const mathHandlers = {
  math: (_state: unknown, node: { value: string }) => ({
    type: 'element',
    tagName: 'div',
    properties: { dataMath: node.value },
    children: [],
  }),
  inlineMath: (_state: unknown, node: { value: string }) => ({
    type: 'element',
    tagName: 'code',
    properties: { className: ['math-inline'] },
    children: [{ type: 'text', value: node.value }],
  }),
} as never;

/** Parse GFM Markdown to an HTML string. Not sanitized — call `sanitizeHtml` after. */
export function markdownToHtml(markdown: string): string {
  // Leading whitespace is extraction-artifact padding, not intentional
  // indentation -- but CommonMark treats a line starting with >=4 spaces
  // (a leading tab already expands to 4 at the first tab stop) as an
  // indented code block, silently skipping markdown/math parsing entirely.
  const withoutLeadingWhitespace = markdown.replace(/^[ \t]+/, '');
  // Extraction wraps things like corresponding-author footnote markers in raw
  // `<em>*</em>` -- a literal `*` sitting flush against a tag boundary is never
  // authored markdown emphasis, but CommonMark's delimiter matching doesn't
  // treat raw HTML as a barrier, so a pair of these gets consumed as an
  // (empty, invisible) emphasis span instead of rendering as `*`.
  const withEscapedSymbolAsterisks = withoutLeadingWhitespace.replace(
    /(?<=>)\*(?!\*)|(?<!\*)\*(?=<)/g,
    '\\*'
  );
  // runSync is typed as unist `Node`; the pipeline only ever yields an mdast root.
  const tree = processor.runSync(
    processor.parse(withEscapedSymbolAsterisks)
  ) as Parameters<typeof toHast>[0];
  const hast = toHast(tree, { allowDangerousHtml: true, handlers: mathHandlers });

  // Browsers collapse raw tabs/runs of whitespace to nothing visible; authors
  // use tabs to space out equation numbers (e.g. "$x=y$\t\t\t(2)"), so convert
  // them to non-breaking spaces to keep that spacing on render.
  return toHtml(hast, { allowDangerousHtml: true }).replace(/\t/g, '&nbsp;');
}

const MATHML_NS = 'http://www.w3.org/1998/Math/MathML';

/** Everything else falls back to a plain `<mtext>`, which always lays out. */
function mathmlTokenTagFor(text: string): 'mn' | 'mi' | 'mtext' {
  if (/^[0-9]+(\.[0-9]+)?$/.test(text)) return 'mn';
  if (/^[A-Za-z]$/.test(text)) return 'mi';
  return 'mtext';
}

/**
 * The extraction service sometimes emits bare text as a direct child of
 * `<math>`/`<mrow>` (e.g. `<math>R</math>`) instead of wrapping it in a
 * token element (`<mi>`, `<mn>`, `<mtext>`). MathML Core -- what browsers
 * actually implement -- has no layout rule for a bare text node there, so it
 * renders as an empty 0x0 box instead of the letter/number it should show.
 */
/** Token elements whose text content is already valid where it sits -- don't recurse into them. */
const MATHML_TOKEN_TAGS = new Set(['mi', 'mn', 'mo', 'mtext', 'ms', 'mspace']);

function wrapBareMathText(node: Element): void {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const tagName = (child as Element).tagName.toLowerCase();

      if (!MATHML_TOKEN_TAGS.has(tagName)) wrapBareMathText(child as Element);
      continue;
    }
    if (child.nodeType !== Node.TEXT_NODE) continue;

    const text = child.textContent?.trim() ?? '';

    if (!text) continue;

    const wrapper = document.createElementNS(MATHML_NS, mathmlTokenTagFor(text));

    wrapper.textContent = text;
    node.replaceChild(wrapper, child);
  }
}

function normalizeMathml(html: string): string {
  if (!html.includes('<math')) return html;

  const host = document.createElement('div');

  host.innerHTML = html;
  for (const math of Array.from(host.querySelectorAll('math'))) {
    wrapBareMathText(math);
  }

  return host.innerHTML;
}

/**
 * Extraction sometimes dumps what's really one or more standalone figures as
 * `![](url)![](url)` inside a plain paragraph block, with no other text --
 * e.g. `<p><img src="..."><img src="..."></p>`. Left as inline images, each
 * would get squashed to a 20px-tall thumbnail behind a click-to-enlarge
 * popup (see InlineImageView) -- appropriate for an equation mid-sentence,
 * wrong for what's meant to be a normal, full-size figure. Unwrapping the
 * `<p>` and rewriting `src` to `data-href` (FigureImage's convention, not
 * InlineImage's `data-inline-href`) instead routes each image through the
 * same block-level figure rendering as any other figure block -- mirroring
 * how `renderFigure` in contractRenderer.ts already emits bare `<img
 * data-href>` siblings with no wrapping `<p>`.
 */
function promoteImageOnlyParagraphs(html: string): string {
  if (!html.includes('<img')) return html;

  const host = document.createElement('div');

  host.innerHTML = html;
  for (const p of Array.from(host.querySelectorAll('p'))) {
    const childNodes = Array.from(p.childNodes);
    const isImageOnly =
      childNodes.length > 0 &&
      childNodes.every((node) =>
        node.nodeType === Node.ELEMENT_NODE
          ? (node as Element).tagName === 'IMG'
          : !node.textContent?.trim()
      );

    if (!isImageOnly) continue;

    for (const img of Array.from(p.querySelectorAll('img[src]'))) {
      const src = img.getAttribute('src');

      img.removeAttribute('src');
      if (src) img.setAttribute('data-href', src);
    }
    p.replaceWith(...childNodes);
  }

  return host.innerHTML;
}

/**
 * Markdown (`![alt](url)`) and raw inline HTML can both embed an `<img
 * src>` mid-paragraph/mid-cell -- e.g. an equation exported as an image, or a
 * small inline figure. That `src` is often an `s3://` href the browser can't
 * fetch directly, and TipTap's schema has no node for a bare `<img src>`
 * anyway (see InlineImage.ts, which -- like FigureImage -- only recognizes
 * `data-inline-href`). Rewrite it here so ProseMirror's parser has something
 * to match instead of silently dropping the tag. Anything already carrying
 * `data-href` (an already-rendered figure block, or an image-only paragraph
 * just promoted above) or `data-inline-href` is left alone.
 */
function rewriteInlineImageHrefs(html: string): string {
  if (!html.includes('<img')) return html;

  const host = document.createElement('div');

  host.innerHTML = html;
  for (const img of Array.from(host.querySelectorAll('img[src]'))) {
    if (img.hasAttribute('data-href') || img.hasAttribute('data-inline-href')) continue;

    const src = img.getAttribute('src');

    img.removeAttribute('src');
    if (src) img.setAttribute('data-inline-href', src);
  }

  return host.innerHTML;
}

/**
 * Strip anything unsafe. `data-*` attributes are preserved by default, which is
 * what carries our block handles through to the editor.
 */
export function sanitizeHtml(html: string): string {
  return rewriteInlineImageHrefs(
    promoteImageOnlyParagraphs(
      normalizeMathml(
        DOMPurify.sanitize(html, {
          USE_PROFILES: { html: true, mathMl: true, svg: true },
        })
      )
    )
  );
}

/** Render a block's content according to its declared format. */
export function renderContentValue(
  format: 'md' | 'html' | 'latex',
  value: string
): string {
  switch (format) {
    case 'md':
      return sanitizeHtml(markdownToHtml(value));
    case 'html':
      return sanitizeHtml(value);
    case 'latex':
      // Handled by the formula branch of the contract renderer; this is a fallback.
      return `<div data-math="${escapeAttribute(value)}"></div>`;
  }
}

export function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
